import api from './api.service';

export interface UptimeMonitor {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  type: 'http' | 'https' | 'tcp' | 'ping' | 'dns';
  url: string;
  method: string;
  headers?: Record<string, string>;
  body?: string;
  interval_seconds: number;
  timeout_seconds: number;
  expected_status_code: number;
  expected_response_contains?: string;
  retry_count: number;
  failure_threshold: number;
  success_threshold: number;
  status: 'up' | 'down' | 'degraded' | 'paused' | 'pending';
  consecutive_failures: number;
  consecutive_successes: number;
  last_check_at?: string;
  last_response_time_ms?: number;
  last_status_code?: number;
  last_error?: string;
  uptime_percentage: number;
  is_active: boolean;
  alert_on_failure: boolean;
  alert_on_recovery: boolean;
  status_page_component_id?: string;
  notification_channels?: string[];
  tags?: string[];
  region?: string;
  ssl_check: boolean;
  ssl_expiry_days_warning?: number;
  created_at: string;
  updated_at: string;
}

export interface UptimeCheck {
  id: string;
  monitor_id: string;
  tenant_id: string;
  status: 'up' | 'down' | 'degraded';
  response_time_ms?: number;
  status_code?: number;
  error_message?: string;
  response_headers?: Record<string, string>;
  response_size_bytes?: number;
  region?: string;
  ssl_info?: any;
  dns_info?: any;
  checked_at: string;
}

export interface UptimeIncident {
  id: string;
  monitor_id: string;
  tenant_id: string;
  status: 'ongoing' | 'resolved';
  started_at: string;
  resolved_at?: string;
  duration_seconds?: number;
  cause?: string;
  checks_failed: number;
  alert_sent: boolean;
  recovery_alert_sent: boolean;
  monitor?: UptimeMonitor;
  created_at: string;
}

export interface UptimeStats {
  uptime_percentage: number;
  avg_response_time_ms: number;
  total_checks: number;
  successful_checks: number;
  failed_checks: number;
  incidents_count: number;
}

export interface CreateMonitorData {
  name: string;
  description?: string;
  type?: 'http' | 'https' | 'tcp' | 'ping' | 'dns';
  url: string;
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  interval_seconds?: number;
  timeout_seconds?: number;
  expected_status_code?: number;
  expected_response_contains?: string;
  retry_count?: number;
  failure_threshold?: number;
  success_threshold?: number;
  alert_on_failure?: boolean;
  alert_on_recovery?: boolean;
  status_page_component_id?: string;
  notification_channels?: string[];
  tags?: string[];
  ssl_check?: boolean;
  ssl_expiry_days_warning?: number;
}

export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface GetAllOptions {
  page?: number;
  limit?: number;
  status?: string;
}

class UptimeMonitorService {
  private baseUrl = '/v1/uptime-monitors';

  async getAll(options?: GetAllOptions): Promise<{ success: boolean; data: UptimeMonitor[]; pagination?: PaginationInfo }> {
    const params: Record<string, any> = {};
    if (options?.page) params.page = options.page;
    if (options?.limit) params.limit = options.limit;
    if (options?.status) params.status = options.status;

    return api.get(this.baseUrl, { params });
  }

  async getById(id: string): Promise<{ success: boolean; data: UptimeMonitor }> {
    return api.get(`${this.baseUrl}/${id}`);
  }

  async create(data: CreateMonitorData): Promise<{ success: boolean; data: UptimeMonitor }> {
    return api.post(this.baseUrl, data);
  }

  async update(id: string, data: Partial<CreateMonitorData>): Promise<{ success: boolean; data: UptimeMonitor }> {
    return api.put(`${this.baseUrl}/${id}`, data);
  }

  async delete(id: string): Promise<{ success: boolean; message: string }> {
    return api.delete(`${this.baseUrl}/${id}`);
  }

  async toggle(id: string): Promise<{ success: boolean; data: UptimeMonitor }> {
    return api.post(`${this.baseUrl}/${id}/toggle`);
  }

  async runCheck(id: string): Promise<{ success: boolean; data: { monitor: UptimeMonitor; checkResult: any } }> {
    return api.post(`${this.baseUrl}/${id}/check`);
  }

  async getHistory(id: string, limit: number = 100): Promise<{ success: boolean; data: UptimeCheck[] }> {
    return api.get(`${this.baseUrl}/${id}/history`, { params: { limit } });
  }

  async getStats(id: string, days: number = 30): Promise<{ success: boolean; data: UptimeStats }> {
    return api.get(`${this.baseUrl}/${id}/stats`, { params: { days } });
  }

  async getIncidents(monitorId?: string, limit: number = 50): Promise<{ success: boolean; data: UptimeIncident[] }> {
    return api.get(`${this.baseUrl}/incidents`, { params: { monitorId, limit } });
  }
}

export default new UptimeMonitorService();
